import _ from 'lodash';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  TableSortLabel,
  Box,
  Typography,
} from '@mui/material';
import { StyledTable } from './style';
import { visuallyHidden } from '@mui/utils';

export type Order = 'asc' | 'desc';

export type IColProps<T> = {
  key: string;
  title: string;
  dataKey: string;
  render?: (data: T, rowIndex: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right' | 'justify';
  valign?: 'top' | 'bottom' | 'middle' | 'baseline';
  width?: string | number;
  className?: string;
  sortable?: boolean;
};

export type ITableProps<T> = {
  columns: IColProps<T>[];
  rowKey: (data: T, index: number) => string;
  rowClassName?: (data: T, index: number) => string | undefined;
  dataSource: T[];
  loading?: boolean;
  onClick?: (rowData: T) => void;
  orderBy?: string;
  order?: Order;
  onOrderChange?: (orderBy?: string, order?: Order) => void;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    onPageChange: (
      event: React.MouseEvent<HTMLButtonElement> | null,
      page: number,
    ) => void;
    onLimitChange?:
      | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
      | undefined;
  };
};

export default function Table<T>(props: ITableProps<T>) {
  // const cursor = props?.onClick ? { cursor: 'pointer' } : { cursor: 'default' };

  const { order, orderBy, columns } = props;

  return (
    <>
      <TableContainer sx={{ flex: 1 }}>
        <StyledTable stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  style={{ width: column.width }}
                  sortDirection={
                    column.sortable && orderBy === column.key ? order : false
                  }
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.key}
                      direction={orderBy === column.key ? order : 'asc'}
                      onClick={() =>
                        props.onOrderChange?.(
                          column.key,
                          orderBy !== column.key || order !== 'asc'
                            ? 'asc'
                            : 'desc',
                        )
                      }
                    >
                      {column.title}
                      {orderBy === column.key ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ borderBottom: 'none', p: 8 }}
                >
                  <CircularProgress sx={{ mt: 2, mb: 2 }} />
                </TableCell>
              </TableRow>
            ) : !props.dataSource.length ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    textAlign: 'center',
                    p: 8,
                    borderBottom: 'none',
                  }}
                >
                  <img
                    className="empty-img"
                    src={process.env.PUBLIC_URL + '/empty.png'}
                    alt="empty"
                  />
                  <Typography>No Data</Typography>
                </TableCell>
              </TableRow>
            ) : (
              props.dataSource.map((row, idx) => {
                return (
                  <TableRow
                    className={props.rowClassName?.(row, idx)}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={props.rowKey(row, idx)}
                    onClick={
                      props.onClick
                        ? () => {
                            props.onClick?.(row);
                          }
                        : undefined
                    }
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell key={column.key} align={column.align}>
                          {column.render
                            ? column.render?.(row, idx)
                            : _.get(row, column.dataKey)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      {props.pagination ? (
        <TablePagination
          disabled={props.loading}
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30, 40, 50]}
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
          component="div"
          count={props.pagination.total}
          rowsPerPage={props.pagination.limit}
          page={props.pagination.page}
          onPageChange={props.pagination.onPageChange}
          onRowsPerPageChange={props.pagination.onLimitChange}
        />
      ) : null}
    </>
  );
}
